import { useCallback, useRef, useState } from "react";

import { useProgressTimer } from "./useProgressTimer";
import { useSessionScore } from "./useSessionScore";
import { useStreak } from "./useStreak";
import { useBestTime } from "./useBestTime";
import { usePointsTarget, type PointsTargetKey } from "../pointsSettings";
import { TARGET_KEY_TO_FLOW } from "../trophies";

/**
 * Score a repeating cycle of work against a per-warehouse time target.
 *
 * Every scoring screen used to hand-roll this: stop the timer, check it didn't
 * expire, ceil the remaining seconds, award them, bump the streak, show a
 * popup, restart. Seven copies existed before dispatch, picking, the drops and
 * wipe were due to join them, so it lives here once instead.
 *
 * The contract is one call per completed unit of work — `complete()` closes the
 * window that unit ran in AND opens the next one, because the cycle being
 * measured is scan-to-scan: the walk back is part of the job.
 *
 * A warehouse with no target for this flow scores nothing. `scoring` is false
 * then, and callers should hide the pill rather than show a trophy on a screen
 * that cannot earn.
 */
export interface CycleScoreApi {
    /** Today's running total, for <ScoreDisplay total={} />. */
    total: number;
    /** False when this warehouse hasn't set a target for the flow. */
    scoring: boolean;
    /** One unit of work finished: award the time saved and start the next. */
    complete: () => void;
    /** Open a window without awarding — entering the flow, or resuming. */
    begin: () => void;
    /** Leave the flow. Nothing is awarded and no new window opens. */
    end: () => void;
    /** Hold the clock while something out of the operator's hands happens. */
    pause: () => void;
    resume: () => void;
    /** Props for <TimeBonusPopup>. seconds === null hides it. */
    bonus: { seconds: number | null; color: "green" | "amber"; streak: number; isBest: boolean; key: number };
}

const BONUS_VISIBLE_MS = 1500;

export const useCycleScore = (
    warehouse: string | undefined,
    targetKey: PointsTargetKey | null,
    bestTimeKey?: string,
): CycleScoreApi => {
    const timer = useProgressTimer();
    const score = useSessionScore();
    const streak = useStreak();
    const bestTime = useBestTime(bestTimeKey ?? "cycle-best-time");
    const target = usePointsTarget(warehouse, targetKey);

    const [bonus, setBonus] = useState<CycleScoreApi["bonus"]>({
        seconds: null, color: "green", streak: 0, isBest: false, key: 0,
    });
    const hideRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const targetRef = useRef<number | null>(target);
    targetRef.current = target;

    const begin = useCallback(() => {
        const ms = targetRef.current;
        if (ms == null) return;
        timer.start(ms);
    }, [timer]);

    const end = useCallback(() => {
        timer.reset();
        streak.reset();
        if (hideRef.current) clearTimeout(hideRef.current);
        setBonus(b => ({ ...b, seconds: null, isBest: false }));
    }, [timer, streak]);

    const complete = useCallback(() => {
        const ms = targetRef.current;
        if (ms == null) return;

        // stop() reads refs, not state, so calling it from an async handler
        // that closed over an older render is safe — and a window that was
        // never opened reports durationMs 0, which awards nothing.
        const { elapsedMs, completed, durationMs } = timer.stop();

        if (durationMs > 0 && !completed) {
            const secondsLeft = Math.max(0, Math.ceil((durationMs - elapsedMs) / 1000));
            const best = bestTime.check(elapsedMs);
            const nextStreak = streak.increment();
            score.addSeconds(secondsLeft, targetKey ? TARGET_KEY_TO_FLOW[targetKey] : undefined);
            setBonus(b => ({
                seconds: secondsLeft,
                color: elapsedMs / durationMs < 0.5 ? "green" : "amber",
                streak: nextStreak,
                // Never flag the first ever time as a personal best.
                isBest: best.isNewBest && best.prevExisted,
                key: b.key + 1,
            }));
            if (hideRef.current) clearTimeout(hideRef.current);
            hideRef.current = setTimeout(
                () => setBonus(b => ({ ...b, seconds: null, isBest: false })),
                BONUS_VISIBLE_MS,
            );
        } else if (durationMs > 0) {
            // Over target. No award and no popup — the streak resets, but
            // running long is never punished beyond that: plenty of units
            // legitimately take longer than the target.
            streak.reset();
            setBonus(b => ({ ...b, seconds: null, isBest: false }));
        }

        timer.start(ms);
    }, [timer, score, streak, bestTime, targetKey]);

    return {
        total: score.total,
        scoring: target != null,
        complete,
        begin,
        end,
        pause: timer.pause,
        resume: timer.resume,
        bonus,
    };
};
